<template>
  <!-- Admin desktop: calendar -->
  <ReviewCalendarPage v-if="showCalendar" />

  <!-- Mobile or reporter: day-by-day assignments -->
  <v-container v-else fluid>
    <v-row>
      <v-col cols="12">
        <!-- Progress -->
        <v-alert
          v-if="todayData && todayData.progress.total > 0"
          :type="todayData.progress.remaining === 0 ? 'success' : 'info'"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          <span v-if="todayData.progress.remaining === 0">
            All {{ todayData.progress.total }} reviews completed today
          </span>
          <span v-else>
            {{ todayData.progress.completed }} of {{ todayData.progress.total }} completed today
            &mdash; {{ todayData.progress.remaining }} remaining
          </span>
        </v-alert>

        <!-- Loading -->
        <div v-if="todayLoading" class="text-center py-8">
          <v-progress-circular indeterminate></v-progress-circular>
          <div class="mt-2">Loading schedule...</div>
        </div>

        <!-- Today's assignments -->
        <template v-if="todayData">
          <!-- Past due section -->
          <v-expansion-panels v-if="todayData.pastDue.length > 0" class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                Past Due ({{ todayData.pastDue.length }} pending)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-card
                  v-for="entry in todayData.pastDue"
                  :key="entry.id"
                  class="mb-3"
                  variant="outlined"
                  color="warning"
                >
                  <v-card-subtitle class="d-flex align-center">
                    <span>{{ formatEntryDate(entry.schedule_date) }}</span>
                    <v-spacer />
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      :loading="refreshingEntryId === entry.id"
                      @click="refreshEntryReviews(entry)"
                      class="mr-1"
                    >
                      <v-icon size="small">mdi-refresh</v-icon>
                      <v-tooltip activator="parent" location="top">New review</v-tooltip>
                    </v-btn>
                    <v-chip size="x-small" :color="getStatusColor(entry.status)" variant="flat">
                      {{ entry.status }}
                    </v-chip>
                  </v-card-subtitle>
                  <v-card-text class="pt-0">
                    <div class="d-flex align-center ga-2 mb-2">
                      <div :style="{ background: getPlatformGradient(entry.platform), width: '22px', height: '22px', borderRadius: '3px', flexShrink: 0 }"></div>
                      <span class="text-body-2">
                        <strong>{{ entry.platform }}</strong> · {{ entry.location }} · {{ entry.brand }}
                      </span>
                    </div>
                    <!-- Entry reviews inline -->
                    <div v-if="entry.completions?.length > 0">
                      <div
                        v-for="c in entry.completions"
                        :key="c.id"
                        class="d-flex align-center ga-2 mb-1 review-completed"
                      >
                        <v-icon icon="mdi-check-circle" color="success" size="small" />
                        <span class="text-body-2">
                          {{
                            c.review_text
                              ? c.review_text.substring(0, 80) +
                                (c.review_text.length > 80 ? "..." : "")
                              : "Screenshot uploaded"
                          }}
                        </span>
                      </div>
                    </div>
                    <div v-if="entry.reviews?.length > 0">
                      <div
                        v-for="review in entry.reviews"
                        :key="review.id"
                        class="d-flex align-start ga-2 py-1 review-row"
                      >
                        <div class="flex-grow-1">
                          <div class="text-body-1">{{ review.text }}</div>
                          <div class="d-flex align-center mt-1">
                            <span class="text-caption">{{ review.rating }}</span>
                            <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                          </div>
                        </div>
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          @click="copyToClipboard(review.text)"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                          <v-tooltip activator="parent" location="top">Copy text</v-tooltip>
                        </v-btn>
                        <v-btn
                          icon
                          size="small"
                          variant="tonal"
                          color="primary"
                          @click="openUpload(entry, review)"
                        >
                          <v-icon>mdi-camera</v-icon>
                          <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                        </v-btn>
                      </div>
                    </div>
                    <div
                      v-else-if="!entry.completions?.length"
                      class="text-medium-emphasis text-body-2"
                    >
                      {{
                        entry.totalReviews === 0
                          ? "No reviews available for this combo"
                          : "All reviews on cooldown"
                      }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Regular user view: grouped by location -->
          <v-card v-for="group in userLocationGroups" :key="group.location" class="mb-4">
            <v-card-subtitle>{{ group.location }}</v-card-subtitle>
            <v-card-text>
              <div
                v-for="(entry, idx) in group.entries"
                :key="entry.id"
                class="mb-3 pb-3"
                :class="{ 'border-b': idx < group.entries.length - 1 }"
              >
                <div class="d-flex align-center ga-2 mb-2">
                  <div :style="{ background: getPlatformGradient(entry.platform), width: '22px', height: '22px', borderRadius: '3px', flexShrink: 0 }"></div>
                  <span class="text-body-2">
                    <strong>{{ entry.platform }}</strong> · {{ entry.brand }}
                  </span>
                  <v-spacer />
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    :loading="refreshingEntryId === entry.id"
                    @click="refreshEntryReviews(entry)"
                  >
                    <v-icon size="small">mdi-refresh</v-icon>
                    <v-tooltip activator="parent" location="top">New review</v-tooltip>
                  </v-btn>
                  <v-chip size="x-small" :color="getStatusColor(entry.status)" variant="flat">
                    {{ entry.completion_count }}/{{ entry.num_reviews }}
                  </v-chip>
                </div>
                <!-- Completions -->
                <div v-if="entry.completions?.length > 0">
                  <div
                    v-for="c in entry.completions"
                    :key="c.id"
                    class="d-flex align-center ga-2 mb-1 review-completed"
                  >
                    <v-icon icon="mdi-check-circle" color="success" size="small" />
                    <span class="text-body-2 flex-grow-1">
                      {{
                        c.review_text
                          ? c.review_text.substring(0, 80) +
                            (c.review_text.length > 80 ? "..." : "")
                          : "Screenshot uploaded"
                      }}
                    </span>
                    <v-btn
                      v-if="c.asset_id"
                      icon
                      size="x-small"
                      variant="text"
                      @click="viewScreenshot(c.asset_id)"
                    >
                      <v-icon size="small">mdi-image</v-icon>
                      <v-tooltip activator="parent" location="top">View screenshot</v-tooltip>
                    </v-btn>
                  </div>
                </div>
                <!-- Review suggestions -->
                <div v-if="entry.reviews?.length > 0">
                  <div
                    v-for="review in entry.reviews"
                    :key="review.id"
                    class="d-flex align-start ga-2 py-1 review-row"
                  >
                    <div class="flex-grow-1">
                      <div class="text-body-1">{{ review.text }}</div>
                      <div class="d-flex align-center mt-1">
                        <span class="text-caption">{{ review.rating }}</span>
                        <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                      </div>
                    </div>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="copyToClipboard(review.text)"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                      <v-tooltip activator="parent" location="top">Copy text</v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      @click="openUpload(entry, review)"
                    >
                      <v-icon>mdi-camera</v-icon>
                      <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                    </v-btn>
                  </div>
                </div>
                <div
                  v-else-if="!entry.completions?.length"
                  class="text-medium-emphasis text-body-2"
                >
                  {{
                    entry.totalReviews === 0
                      ? "No reviews available for this combo"
                      : "All reviews on cooldown"
                  }}
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-empty-state
            v-if="todayData.today.length === 0"
            class="text-center py-8"
            headline="No reviews scheduled for today"
            text="Check back later or ask an admin to generate the schedule"
            icon="mdi-calendar-blank-outline"
          />
        </template>
      </v-col>
    </v-row>

    <!-- Completion Dialog -->
    <ReviewCompletionDialog
      v-model="completionDialogOpen"
      :entry="selectedScheduleEntry"
      :review="selectedReview"
      @completed="onCompleted"
    />

    <!-- Screenshot viewer -->
    <v-dialog v-model="screenshotDialog" max-width="600" :fullscreen="mobile">
      <v-card>
        <v-toolbar color="primary" density="compact">
          <v-toolbar-title>Screenshot</v-toolbar-title>
          <v-spacer />
          <v-btn icon :size="mobile ? 'default' : 'small'" @click="screenshotDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="text-center pa-2">
          <v-progress-circular v-if="screenshotLoading" indeterminate class="my-8" />
          <v-img v-else-if="screenshotUrl" :src="screenshotUrl" max-height="80vh" contain />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import api from "@/utils/api.js";
import { useApp } from "@/composables/useApp";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { useReviewSchedule } from "@/composables/useReviewSchedule.js";
import { useDisplay } from "vuetify";

import ReviewCalendarPage from "@/views/reviews/ReviewCalendarPage.vue";
import ReviewCompletionDialog from "@/components/ReviewCompletionDialog.vue";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const { isAdmin } = useApp();
const { smAndDown, mobile } = useDisplay();
// Always show calendar when accessed via /reviews-calendar route, regardless of screen size
const route = useRoute();
const showCalendar = computed(() => route.name === "ReviewsCalendar" || (isAdmin.value && !mobile.value));
const { setActions, clearActions } = useAppBarActions();

const {
  todayData,
  todayLoading,
  snackbar,
  fetchTodayAssignments,
  completionDialogOpen,
  selectedScheduleEntry,
  selectedReview,
  openCompletionDialog,
  closeCompletionDialog,
} = useReviewSchedule();

const { getPlatformGradient } = usePlatformColors();

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "partial":
      return "warning";
    case "skipped":
      return "error";
    default:
      return "grey";
  }
};

const formatEntryDate = (dateStr) => {
  if (!dateStr) return "";
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

// Group today entries by location (regular user)
const userLocationGroups = computed(() => {
  if (!todayData.value?.today) return [];
  const map = new Map();
  for (const entry of todayData.value.today) {
    if (!map.has(entry.location)) map.set(entry.location, []);
    map.get(entry.location).push(entry);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([location, entries]) => ({ location, entries }));
});

const refreshingEntryId = ref(null);
const screenshotDialog = ref(false);
const screenshotUrl = ref(null);
const screenshotLoading = ref(false);

async function viewScreenshot(assetId) {
  screenshotDialog.value = true;
  screenshotLoading.value = true;
  screenshotUrl.value = null;
  try {
    const res = await api.assetsApi.get(`/api/assets/${assetId}/download`);
    screenshotUrl.value = res.data.url;
  } catch {
    snackbar.value = { show: true, message: "Failed to load screenshot", color: "error" };
    screenshotDialog.value = false;
  } finally {
    screenshotLoading.value = false;
  }
}

async function refreshEntryReviews(entry) {
  refreshingEntryId.value = entry.id;
  try {
    // Reviews are now embedded in the assignment — no separate refresh needed
    // Triggering a full re-fetch of the calendar page instead
    snackbar.value = { show: true, message: "Reviews are part of assignments now", color: "info" };
  } catch {
    snackbar.value = { show: true, message: "Failed to refresh reviews", color: "error" };
  } finally {
    refreshingEntryId.value = null;
  }
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    snackbar.value = { show: true, message: "Copied to clipboard", color: "success" };
  } catch {
    snackbar.value = { show: true, message: "Failed to copy", color: "error" };
  }
}

function openUpload(entry, review = null) {
  openCompletionDialog(entry, review);
}

async function onCompleted() {
  closeCompletionDialog();
  await fetchTodayAssignments();
}

// When day view is active, set app bar actions and fetch data
const dayDataLoaded = ref(false);

watchEffect(() => {
  if (!showCalendar.value) {
    setActions([
      {
        icon: "mdi-refresh",
        tooltip: "Refresh",
        onClick: fetchTodayAssignments,
        loading: todayLoading.value,
      },
    ]);
  }
});

watch(showCalendar, (isCalendar) => {
  if (!isCalendar && !dayDataLoaded.value) {
    dayDataLoaded.value = true;
    fetchTodayAssignments();
  }
}, { immediate: true });

onBeforeUnmount(() => clearActions());
</script>

<style>
.review-completed {
  opacity: 0.6;
}

.review-row {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 4px;
}

.review-row:last-child {
  border-bottom: none;
}

.border-b {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
